import {
  Component,
  CUSTOM_ELEMENTS_SCHEMA,
  Input,
  Output,
  EventEmitter,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  inject,
  OnInit,
  OnDestroy,
} from '@angular/core';
import { CommonModule, CurrencyPipe, DecimalPipe } from '@angular/common';
import { Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';
import { FileUploadService } from '../../../../../core/services/file-upload.service';
import {
  ParsedFileData,
  FileTotals,
  GridColumn,
} from '../../../../../core/models/file-upload.model';
import { UploadStatus } from '../../../../../core/enums/upload-status.enum';

/**
 * Grid column configuration per specification
 */
const GRID_COLUMNS: GridColumn[] = [
  { field: 'fileName', header: 'File Name', type: 'text' },
  { field: 'country', header: 'Country', type: 'text' },
  { field: 'uploadStatus', header: 'Upload Status', type: 'status' },
  {
    field: 'checkCount',
    header: 'Check Count',
    type: 'number',
    showTotal: true,
  },
  { field: 'ddaCount', header: 'DDA Count', type: 'number', showTotal: true },
  {
    field: 'docsInFile',
    header: 'Docs in File',
    type: 'number',
    showTotal: true,
  },
  {
    field: 'totalAmount',
    header: 'Total $ Amount of Docs',
    type: 'currency',
    showTotal: true,
  },
  { field: 'action', header: 'Action', type: 'action' },
];

@Component({
  selector: 'app-uploaded-files-grid',
  standalone: true,
  schemas: [CUSTOM_ELEMENTS_SCHEMA],
  imports: [CommonModule, CurrencyPipe, DecimalPipe],
  templateUrl: './uploaded-files-grid.component.html',
  styleUrls: ['./uploaded-files-grid.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class UploadedFilesGridComponent implements OnInit, OnDestroy {
  private readonly fileUploadService = inject(FileUploadService);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly destroy$ = new Subject<void>();

  @Input() parsedFiles: ParsedFileData[] = [];
  @Input() totals: FileTotals = {
    totalCheckCount: 0,
    totalDdaCount: 0,
    totalDocsInFile: 0,
    totalAmount: 0,
  };

  @Output() fileRemoved = new EventEmitter<string>();

  /** Column definitions */
  readonly columns = GRID_COLUMNS;

  readonly UploadStatus = UploadStatus;

  /** Country warning note text */
  readonly countryWarningText =
    'When uploading files for multiple Countries, please ensure that each request contains files for a single Country. Example: US Files should be separated from files from a different country in a Request.';

  ngOnInit(): void {
    // Wire grid to FileUploadService
    this.fileUploadService.uploadState$
      .pipe(takeUntil(this.destroy$))
      .subscribe(state => {
        this.parsedFiles = state.parsedFiles;
        this.totals = state.totals;
        // Manually trigger change detection since we're using OnPush
        this.cdr.markForCheck();
      });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle remove file action
   */
  onRemoveFile(fileId: string): void {
    this.fileUploadService.removeFile(fileId);
    this.fileRemoved.emit(fileId);
  }

  /**
   * Get display value for a cell
   */
  getCellValue(file: ParsedFileData, column: GridColumn): string | number {
    if (column.field === 'action') {
      return '';
    }
    const field = column.field;
    const value = file[field];
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return value ?? '';
  }

  /**
   * Get numeric value for a cell (for number/currency formatting)
   */
  getNumericCellValue(file: ParsedFileData, column: GridColumn): number {
    const value = file[column.field as keyof ParsedFileData];
    return typeof value === 'number' ? value : 0;
  }

  /**
   * Format currency values
   */
  formatCurrency(value: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(value);
  }

  /**
   * Format number values
   */
  formatNumber(value: number): string {
    return new Intl.NumberFormat('en-US').format(value);
  }

  /**
   * Check if grid has data
   */
  get hasData(): boolean {
    return this.parsedFiles.length > 0;
  }

  /**
   * Get total value for a column
   */
  getTotalForColumn(column: GridColumn): number {
    switch (column.field) {
      case 'checkCount':
        return this.totals.totalCheckCount;
      case 'ddaCount':
        return this.totals.totalDdaCount;
      case 'docsInFile':
        return this.totals.totalDocsInFile;
      case 'totalAmount':
        return this.totals.totalAmount;
      default:
        return 0;
    }
  }

  /**
   * Get CSS class for status cell
   */
  getStatusClass(status: string): string {
    switch (status.toLowerCase()) {
      case 'uploaded':
        return 'status-valid';
      case 'failed':
        return 'status-error';
      case 'uploading':
        return 'status-uploading';
      default:
        return 'status-pending';
    }
  }

  /**
   * Track by function for ngFor
   */
  trackByFileId(_index: number, file: ParsedFileData): string {
    return file.fileId;
  }
}
